import { useEffect, useState } from "react";
import { messageFor } from "@/lib/errors";
import type { AuthedRequester } from "@/lib/brokerAuth";
import {
  documentTypeLabel,
  getDocumentTypes,
  isSingularDocumentType,
  type DocumentRules,
  type DocumentSurface,
  type DocumentTypeInfo,
} from "@/lib/brokerDocumentTypes";

// BRK-S145 — PMS's document vocabulary + upload rules, read once per surface
// that needs them.
//
// A hook rather than a module-level cache. A cache would be client state that
// outlives a request (NN #9) and, worse, would hold a broker's session's rules
// across a sign-out; the read is one small GET and every surface that wants it
// mounts once.

export interface DocumentTypesState {
  types: DocumentTypeInfo[];
  /** null while loading, and after a failed read. */
  rules: DocumentRules | null;
  /** PMS's own sentence when the read failed, else null. */
  error: string | null;
  /**
   * A type key → its label, with a humanised key for a type this bundle has not
   * heard of. Safe to call before the read settles: it degrades to the
   * humanised key rather than throwing or rendering blank, so a row never waits
   * on a label to exist.
   */
  labelFor: (key: string) => string;
  /** Whether a newer version of this type REPLACES the older (PMS-S335) — see
   *  `isSingularDocumentType` for why an unknown type defaults to true. */
  isSingular: (key: string) => boolean;
}

/**
 * Read `GET /api/broker/document-types`.
 *
 * `enabled` exists so a surface asks only when it will use the answer — the
 * bond wizard's configuration may declare no `files` field at all, and reading
 * the vocabulary on every step of every order would be a request made for
 * nothing. A disabled hook reports "loading" forever, which is correct: nothing
 * consults it.
 */
export function useDocumentTypes(
  request: AuthedRequester,
  enabled = true,
  /** Whose uploads: the bond order's, or a submission's (BRK-S361). */
  surface: DocumentSurface = "bond_order",
): DocumentTypesState {
  const [types, setTypes] = useState<DocumentTypeInfo[]>([]);
  const [rules, setRules] = useState<DocumentRules | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      try {
        const catalog = await getDocumentTypes(request, surface);
        if (cancelled) return;
        setTypes(catalog.types);
        setRules(catalog.rules);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        // A handled state, never a throw into the render (NN #7). The caller
        // renders a way forward; it must not render a drop zone.
        setRules(null);
        setError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [request, enabled, surface]);

  return {
    types,
    rules,
    error,
    labelFor: (key: string) => documentTypeLabel(types, key),
    isSingular: (key: string) => isSingularDocumentType(types, key),
  };
}
